/**
 * TeamComparisonView - Side-by-side KPI comparison of teams
 *
 * Displays a table with cost and productivity KPIs across selected teams,
 * highlighting best/worst values.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, RefreshCw, TrendingUp, TrendingDown, DollarSign, Fuel, Wrench, Users, MapPin } from 'lucide-react';
import { teamAPI } from '../../api';

const PERIOD_OPTIONS = ['month', 'quarter', 'year'];

// Les libelles sont resolus au rendu (teams.comparison.*), jamais figes dans la constante.
const KPI_ROWS = [
  { section: 'costs', rows: [
    { key: 'fuel_total', labelKey: 'fuelTotal', path: 'cost.fuel_total', format: 'eur', lower_is_better: true },
    { key: 'fuel_per_km', labelKey: 'fuelPerKm', path: 'cost.fuel_per_km', format: 'eur_decimal', lower_is_better: true },
    { key: 'maintenance_total', labelKey: 'maintenanceTotal', path: 'cost.maintenance_total', format: 'eur', lower_is_better: true },
    { key: 'maintenance_per_vehicle', labelKey: 'maintenancePerVehicle', path: 'cost.maintenance_per_vehicle', format: 'eur', lower_is_better: true },
    { key: 'tire_cost', labelKey: 'tireCost', path: 'cost.tire_cost', format: 'eur', lower_is_better: true },
    { key: 'total', labelKey: 'total', path: 'cost.total', format: 'eur', lower_is_better: true, highlight: true },
  ]},
  { section: 'productivity', rows: [
    { key: 'total_km', labelKey: 'totalKm', path: 'productivity.total_km', format: 'km', lower_is_better: false },
    { key: 'missions_completed', labelKey: 'missionsCompleted', path: 'productivity.missions_completed', format: 'int', lower_is_better: false },
    { key: 'missions_total', labelKey: 'missionsTotal', path: 'productivity.missions_total', format: 'int', lower_is_better: false },
    { key: 'availability_rate', labelKey: 'availabilityRate', path: 'productivity.availability_rate', format: 'percent', lower_is_better: false },
  ]},
];

function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, key) => acc?.[key], obj) ?? 0;
}

function formatValue(value, format) {
  const num = parseFloat(value) || 0;
  switch (format) {
    case 'eur': return `${num.toFixed(0)} EUR`;
    case 'eur_decimal': return `${num.toFixed(2)} EUR`;
    case 'km': return `${num.toFixed(0)} km`;
    case 'percent': return `${num.toFixed(0)}%`;
    case 'int': return `${Math.round(num)}`;
    default: return String(num);
  }
}

export default function TeamComparisonView({ teamIds, onBack }) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState('month');

  const { data: compareData, isLoading } = useQuery({
    queryKey: ['team-compare', teamIds, period],
    queryFn: () => teamAPI.compare(teamIds, period),
    enabled: teamIds.length >= 2,
  });

  const teams = compareData?.data?.teams || [];

  // Find best/worst for each row
  const getHighlightClass = (rowConfig, value, allValues) => {
    if (allValues.length < 2) return '';
    const nums = allValues.map(v => parseFloat(v) || 0);
    const numVal = parseFloat(value) || 0;
    const max = Math.max(...nums);
    const min = Math.min(...nums.filter(n => n > 0)); // ignore 0 for min

    if (nums.every(n => n === numVal)) return ''; // All equal

    if (rowConfig.lower_is_better) {
      if (numVal === min && min > 0) return 'bg-green-50 text-green-700 font-semibold';
      if (numVal === max && max > 0) return 'bg-red-50 text-red-700';
    } else {
      if (numVal === max && max > 0) return 'bg-green-50 text-green-700 font-semibold';
      if (numVal === min && min < max) return 'bg-red-50 text-red-700';
    }
    return '';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{t('teams.comparison.title')}</h1>
              <p className="text-sm text-gray-500">{t('teams.comparison.subtitle', { count: teamIds.length })}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {PERIOD_OPTIONS.map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                  period === p
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t(`teams.detail.period.${p}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Comparison Table */}
      {isLoading ? (
        <div className="bg-white rounded-lg shadow-sm p-12 flex justify-center">
          <RefreshCw className="h-6 w-6 animate-spin text-indigo-500" />
        </div>
      ) : teams.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center text-gray-500">
          {t('teams.comparison.empty')}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              {/* Header row with team names */}
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 bg-gray-50 font-medium text-gray-700 w-48">{t('teams.comparison.kpiHeader')}</th>
                  {teams.map((tm, i) => (
                    <th key={i} className="text-center p-3 min-w-[140px]">
                      {tm.error ? (
                        <span className="text-red-500 text-xs">{tm.error}</span>
                      ) : (
                        <div>
                          <div
                            className="w-full h-1 rounded-full mb-2"
                            style={{ backgroundColor: tm.team?.color || '#3B82F6' }}
                          />
                          <p className="font-semibold text-gray-900">{tm.team?.name || t('teams.comparison.teamFallback', { id: tm.team_id })}</p>
                          <p className="text-xs text-gray-500">{tm.team?.code}</p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {t('teams.comparison.memberSummary', {
                              drivers: tm.kpis?.members?.drivers || 0,
                              vehicles: tm.kpis?.members?.vehicles || 0,
                              tools: tm.kpis?.members?.tools || 0,
                            })}
                          </p>
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {KPI_ROWS.map((section) => (
                  <React.Fragment key={section.section}>
                    {/* Section header */}
                    <tr>
                      <td colSpan={teams.length + 1} className="px-3 py-2 bg-gray-50 font-semibold text-gray-800 text-xs uppercase tracking-wide">
                        {t(`teams.comparison.sections.${section.section}`)}
                      </td>
                    </tr>
                    {/* KPI rows */}
                    {section.rows.map((row) => {
                      const values = teams.map(tm => tm.error ? 0 : getNestedValue(tm.kpis, row.path));
                      return (
                        <tr key={row.key} className={`border-b last:border-b-0 ${row.highlight ? 'bg-indigo-50/30' : ''}`}>
                          <td className={`px-3 py-2.5 text-gray-700 ${row.highlight ? 'font-semibold' : ''}`}>
                            {t(`teams.comparison.rows.${row.labelKey}`)}
                          </td>
                          {teams.map((tm, i) => {
                            const val = values[i];
                            const hlClass = tm.error ? '' : getHighlightClass(row, val, values);
                            return (
                              <td key={i} className={`text-center px-3 py-2.5 ${hlClass}`}>
                                {tm.error ? '-' : formatValue(val, row.format)}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
